import React from 'react'
import { Card, Tag, Tooltip } from 'antd'
import moment from 'moment'
import { getDueStatus, getAlertTypeColor, severityTag, extractBodyContent, formatAlertTypeLabel } from './utils'
import './styles.css'

function AlertCard({ alert, relatedCount = 0, onClick, isDark = false }) {
  const dueStatus = getDueStatus(alert)
  const daysSinceCreation = moment().diff(moment(alert.alertDate || alert.createdAt), 'days')
  const alertTypeLabel = formatAlertTypeLabel(alert.type) || alert.type || 'ALERT'

  const getSeverityBgColor = (sev) => {
    const tag = severityTag(sev)
    const colorMap = {
      red: '#ff5630',
      orange: '#ffab00',
      yellow: '#ffc107',
      green: '#52c41a',
      blue: '#0065ff'
    }
    return colorMap[tag.color] || colorMap.blue
  }

  const locBits = [alert.location, alert.subLocation].filter(Boolean)
  const cardBackground = isDark ? '#434f5a' : '#FCFCFC'
  const cardShadow = isDark ? '0 1px 3px rgba(0, 0, 0, 0.35)' : '0 1px 3px rgba(9, 30, 66, 0.15)'
  const mutedText = isDark ? '#a1a1a1' : '#6b778c'
  const unassignedColor = isDark ? '#c9d2dc' : '#8c8c8c'

  return (
    <Card
      className="alert-card"
      onClick={() => onClick(alert)}
      size="small"
      style={{
        marginBottom: '4px',
        cursor: 'pointer',
        backgroundColor: cardBackground,
        borderRadius: '8px',
        boxShadow: cardShadow,
        transition: 'transform 0.1s ease, box-shadow 0.1s ease'
      }}
      bodyStyle={{ padding: '4px 6px' }}
    >
      <div className="card-top-row">
        <span className="alert-id">{alert.id}</span>
        {relatedCount > 0 && (
          <Tag color="red" style={{
            fontSize: '9px',
            padding: '0px 6px',
            borderRadius: '15px',
            marginRight: '0px',
            marginLeft: '0px',
            fontWeight: 700,
            border: '1px solid #ff4d4f'
          }}>
            +{relatedCount} LINKED
          </Tag>
        )}
        {daysSinceCreation !== null && (
          <Tag color="green" style={{
            fontSize: '9px', padding: '0px 4px',
            borderRadius: '15px', marginRight: '0px', marginLeft: '0px'
          }}>
            {daysSinceCreation} {daysSinceCreation === 1 ? 'day' : 'days'}
          </Tag>
        )}
        {dueStatus && (
          <Tooltip title={moment(alert.dueAt).format('DD-MM-YYYY') || 'No date'}>
            <Tag
              color={dueStatus.status === 'overdue' ? 'red' : dueStatus.status === 'due' ? 'orange' : 'blue'}
              style={{
                fontSize: '9px',
                padding: '0px 3px',
                borderRadius: '15px',
                maxWidth: '150px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                cursor: 'pointer',
                marginRight: '0px', marginLeft: '0px',
              }}
            >
              {dueStatus.status === 'overdue' && <span className="sla-badge-past-indicator" />}
              {dueStatus.status === 'overdue'
                ? `Overdue ${dueStatus.days}d`
                : dueStatus.status === 'due'
                  ? 'Due today'
                  : `Due in ${dueStatus.days}d`
              }
            </Tag>
          </Tooltip>
        )}
      </div>

      <div className="card-title" dangerouslySetInnerHTML={{ __html: alert.title || '' }} />

      {alert.body && (
        <div
          style={{
            fontSize: 11,
            color: mutedText,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2',
            maxHeight: '6em',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            marginBottom: 4
          }}
          dangerouslySetInnerHTML={{ __html: extractBodyContent(alert.body || '') }}
        />
      )}

      <div className="card-line">
        <span>
          {(() => {
            const parts = []
            if (alert.assetModel) parts.push(alert.assetModel)
            if (alert.assetSubType) parts.push(alert.assetSubType)
            if (alert.assetType) parts.push(alert.assetType)

            return parts.length > 0 ? parts.join(' / ') : '—'
          })()}
        </span>
        <span>{alert.manpowerName || alert.assetCode || '—'}</span>
      </div>

      <div className="card-line">
        <span>{locBits.join(' · ') || '—'}</span>
        <span>
          {alert.assignee === 'Unassigned' ? (
            <span style={{ color: unassignedColor, fontStyle: 'italic' }}>Unassigned</span>
          ) : (
            alert.assignee || 'Unassigned'
          )}
        </span>
      </div>

      <div className="card-tags" style={{ marginTop: '2px' }}>
        <Tag
          style={{
            background: getSeverityBgColor(alert.severity),
            color: '#fff',
            fontWeight: 'bold',
            fontSize: '10px',
            borderRadius: '15px'
          }}
        >
          {alert.severity.toUpperCase()}
        </Tag>
        {(() => {
          const typeColor = getAlertTypeColor(alert.type)
          return (
            <Tag
              style={{
                background: typeColor.bg,
                color: typeColor.color,
                fontWeight: 'bold',
                fontSize: '10px',
                borderRadius: '15px'
              }}
            >
              {alertTypeLabel}
            </Tag>
          )
        })()}
      </div>

      <div className="card-footer">
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <span>{moment(alert.createdAt).format('DD-MM-YYYY HH:mm')}</span>
        </div>
        {alert.dueAt && (
          <div style={{ width: '100%', textAlign: 'right' }}>
            <span style={{ fontWeight: 'bold' }}>
              Due: {moment(alert.dueAt).format('DD-MM-YYYY HH:mm')}
            </span>
          </div>
        )}
      </div>
    </Card>
  )
}

export default React.memo(AlertCard)
